import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import { journeys } from "@/mocks/events";
import { journeyTimeline } from "@/mocks/journeys";
import { articles } from "@/mocks/journal";
import JourneyStopDetail from "@/pages/journeys/components/JourneyStopDetail";
import type { JourneyStop } from "@/types/content";

export default function JourneyDetailPage() {
  const { slug } = useParams();

  const stop = journeyTimeline.find((s) => s.slug === slug) as
    | JourneyStop
    | undefined;
  if (stop) {
    return <JourneyStopDetail stop={stop} />;
  }

  const journey = journeys.find((j) => j.slug === slug);

  if (!journey) {
    return (
      <SiteLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-5 text-center">
          <p className="eyebrow">Journey not found</p>
          <h1 className="mt-5 font-heading text-3xl text-foreground-950 md:text-5xl">
            That journey isn't in the archive.
          </h1>
          <Link to="/culinary-journeys" className="btn-primary mt-9">
            Back to Journeys
            <i className="ri-arrow-left-line text-base" />
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = articles[0];
  const related2 = articles[3] ?? articles[0];

  return (
    <SiteLayout>
      <section className="relative flex min-h-[80vh] items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={journey.image}
            alt={`${journey.city} — ${journey.title}`}
            title={`${journey.city} culinary journey`}
            className="h-full w-full object-cover object-center animate-kenburns"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/30" />

        <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 pb-14 md:pb-20">
          <div className="max-w-4xl">
            <Reveal>
              <p className="eyebrow text-primary-400">
                {journey.category} · {journey.region}
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="mt-5 font-heading text-5xl leading-[0.98] text-background-50 sm:text-6xl md:text-7xl lg:text-8xl">
                {journey.city}
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-4 font-heading text-xl italic text-background-50/85 md:text-2xl">
                {journey.title}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="border-b border-foreground-950/10 bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16">
          <div className="grid grid-cols-2 gap-6 py-7 md:grid-cols-4">
            {[
              { k: "Date", v: journey.dateLabel },
              { k: "Location", v: journey.city },
              { k: "Region", v: journey.region },
              { k: "Type", v: journey.category },
            ].map((item) => (
              <div key={item.k}>
                <div className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                  {item.k}
                </div>
                <div className="mt-2 font-body text-sm text-foreground-900">
                  {item.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-24">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="eyebrow">The Journey</p>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <Reveal delay={120}>
                <p className="font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">
                  {journey.excerpt}
                </p>
              </Reveal>
              <Reveal delay={200}>
                <p className="mt-7 font-body text-base leading-relaxed text-foreground-700 md:text-lg">
                  {journey.description}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="w-full px-5 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {journey.gallery.map((src, i) => (
              <Reveal key={src} variant="mask" delay={i * 110}>
                <div
                  className={`w-full overflow-hidden bg-foreground-900 image-zoom ${
                    i === 0 ? "aspect-[4/5]" : "aspect-[4/3] md:aspect-[4/5]"
                  }`}
                >
                  <img
                    src={src}
                    alt={`${journey.city} — gallery image ${i + 1}`}
                    title={`${journey.city} — Chef Eddie G`}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="eyebrow">The Story</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 sm:text-4xl md:text-5xl">
                  {journey.storyTitle}
                </h2>
              </Reveal>
              <Reveal delay={220}>
                <p className="mt-7 font-body text-base leading-relaxed text-foreground-700 md:text-lg">
                  {journey.story}
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-5 lg:col-start-8">
              <Reveal>
                <p className="eyebrow">What Eddie Will Be Doing</p>
              </Reveal>
              <div className="mt-8 divide-y divide-foreground-950/12 border-t border-foreground-950/12">
                {journey.highlights.map((h, i) => (
                  <Reveal key={h} delay={160 + i * 80}>
                    <div className="flex items-start gap-4 py-5">
                      <span className="font-heading text-lg italic text-primary-500">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="font-body text-sm leading-relaxed text-foreground-700">
                        {h}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-24">
          <Reveal>
            <p className="eyebrow">Related Story from the Chef's Table</p>
          </Reveal>
          <Reveal delay={120}>
            <Link
              to={`/journal/${related.slug}`}
              className="group mt-10 grid grid-cols-1 items-center gap-8 border-t border-foreground-950/12 pt-10 md:grid-cols-12"
            >
              <div className="md:col-span-4">
                <div className="aspect-[4/3] w-full overflow-hidden bg-foreground-900 image-zoom">
                  <img
                    src={related.image}
                    alt={related.title}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>
              <div className="md:col-span-7 md:col-start-6">
                <span className="font-label text-[10px] uppercase tracking-label text-primary-600">
                  {related.category}
                </span>
                <h3 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">
                  {related.title}
                </h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-foreground-700">
                  {related.excerpt}
                </p>
                <span className="link-underline mt-5 inline-flex items-center gap-2 font-label text-[11px] uppercase tracking-label text-primary-700">
                  Read Story
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 pb-20 md:pb-28">
          <div className="grid grid-cols-1 gap-8">
            <div className="grid grid-cols-1 gap-8 rounded-sm border border-foreground-950/12 p-8 md:grid-cols-12 md:items-center md:p-12">
              <div className="md:col-span-7">
                <Reveal>
                  <p className="eyebrow">Continue the Journey</p>
                </Reveal>
                <Reveal delay={120}>
                  <h3 className="mt-4 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
                    Bring this journey{" "}
                    <span className="italic text-primary-600">to your city.</span>
                  </h3>
                </Reveal>
                <Reveal delay={200}>
                  <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-foreground-700">
                    Related reading, or start a conversation about hosting Eddie
                    where you are.
                  </p>
                </Reveal>
              </div>
              <div className="flex flex-col gap-4 md:col-span-5 md:items-end">
                <Link to="/invite-eddie" className="btn-primary w-full md:w-auto">
                  Invite Eddie
                  <i className="ri-send-plane-line text-base" />
                </Link>
                <Link
                  to={`/journal/${related2.slug}`}
                  className="inline-flex w-full items-center justify-center gap-3 whitespace-nowrap rounded-md border border-foreground-950/25 px-8 py-4 font-label text-[11px] uppercase tracking-label text-foreground-950 transition-all duration-500 hover:bg-foreground-950 hover:text-background-50 md:w-auto"
                >
                  Related Reading
                  <i className="ri-book-open-line text-base" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        eyebrow="All Journeys"
        title={
          <>
            There are more
            <br />
            <span className="italic text-primary-400">than one road.</span>
          </>
        }
        text="Head back to the full calendar, or start a conversation about the next journey."
        primary={{ label: "Back to Journeys", to: "/culinary-journeys" }}
        secondary={{ label: "Submit a Locavore", to: "/submit-a-locavore" }}
      />
    </SiteLayout>
  );
}